import "./styles.scss";
import { DesignerApplicationSteps } from "../../../__generated__/types";
import { IconTitleDescription } from "../../../components/icon-title-description";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router";
import EastIcon from "@mui/icons-material/East";
import { useUserContext } from "../../../hooks/userContext";
import { Pages } from "../../../utils";
import { Body2Typography } from "../../../components/typography";
import { useState, useEffect } from "react";
import { useStartCourseEnrollment } from "../../../hooks/use-start-course-enrollment";
import { useGetCourseEnrollmentStatus } from "../../../hooks/use-get-course-enrollment-status";
import { useDesignerOnboardingAccess } from "../../../hooks/useDesignerOnboardingAccess";
import { useQuery } from "@apollo/client";
import { GET_HOMEOWNER_STEP_STATUS } from "../../../graphql/homeowner-matching-profile";
import { GET_HOMEOWNER_PROJECTS } from "../../../graphql/homeowner-project";

const calendarLink =
  "https://api.leadconnectorhq.com/widget/bookings/hdg-interview";
const MEETING_SCHEDULED_STORAGE_KEY = "hdg_designer_meeting_scheduled";

const normalizeSubscriptionToken = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }

  if (value && typeof value === "object") {
    const wrapped = value as
      | { designerValue?: unknown; homeownerValue?: unknown; value?: unknown; tier?: unknown }
      | null;
    const candidate =
      wrapped?.designerValue ??
      wrapped?.homeownerValue ??
      wrapped?.value ??
      wrapped?.tier;
    return typeof candidate === "string" ? candidate.trim().toLowerCase() : "";
  }

  return "";
};

const stepTitles = {
  [DesignerApplicationSteps.CompleteApplication]: "Application In Progress",
  [DesignerApplicationSteps.UnderReview]: "Under Review",
  [DesignerApplicationSteps.Approved]: "Approved",
  [DesignerApplicationSteps.MembershipActive]: "Membership Active",
  [DesignerApplicationSteps.Rejected]: "Rejected",
};
const stepDescriptions = {
  [DesignerApplicationSteps.CompleteApplication]:
    "Submit your application for approval",
  [DesignerApplicationSteps.UnderReview]:
    "Our team is reviewing your documents. We will contact you for a virtual meeting in the next 48 hours.",
  [DesignerApplicationSteps.Approved]:
    "Application is approved proceed to payment.",
  [DesignerApplicationSteps.MembershipActive]:
    "Finish setting up your account to get your matching started.",
  [DesignerApplicationSteps.Rejected]:
    "Your application was not approved. Please reach out to the HDG team for next steps.",
};

// Removed unused enums and constants

const stepsInOrder = Object.keys(stepTitles) as DesignerApplicationSteps[];

export const ApplicationStatusBlock = ({
  currentStatus = DesignerApplicationSteps.CompleteApplication,
  accountType = "designer",
  onComplete,
}: {
  currentStatus?: DesignerApplicationSteps;
  accountType?: "designer" | "homeowner";
  onComplete?: () => void;
}) => {
  const { loading, user } = useUserContext();
  const navigate = useNavigate();
  const { startCourseEnrollmentMutation, loading: courseLoading } = useStartCourseEnrollment();
  const { getCourseEnrollmentStatus, data: courseData } = useGetCourseEnrollmentStatus();
  const { isMatchingProfileUnlocked, isBioUnlocked, isDesignerFilesUnlocked, isOnboardingComplete, onboardingStatus } = useDesignerOnboardingAccess();

  const [hasScheduledMeeting, setHasScheduledMeeting] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Get homeowner matching profile status
  const { data: homeownerStepStatusData } = useQuery(GET_HOMEOWNER_STEP_STATUS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: accountType !== "homeowner" || !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: homeownerProjectsData } = useQuery(GET_HOMEOWNER_PROJECTS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: accountType !== "homeowner" || !user?.id,
    fetchPolicy: "cache-and-network",
  });

  // Check course completion status on component mount
  useEffect(() => {
    if (user?.id && accountType === "homeowner") {
      // Initial check
      getCourseEnrollmentStatus({
        variables: { userId: user.id }
      });
      
      // Reload page when user returns from course tab
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          // Reload the page to refresh course completion status from DB
          window.location.reload();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user?.id, accountType, getCourseEnrollmentStatus]);

  // Update completed steps based on course status
  useEffect(() => {
    // Check if the designer already scheduled a meeting (set on the success page)
    if (typeof window !== "undefined") {
      const storageKeyForUser = `${MEETING_SCHEDULED_STORAGE_KEY}${user?.id ? `:${user.id}` : ""}`;
      const raw = localStorage.getItem(storageKeyForUser);
      let scheduled = false;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object" && "userId" in parsed) {
            scheduled = parsed.userId === user?.id;
          }
        } catch {
          // Ignore malformed/legacy data to avoid disabling other users
          scheduled = false;
        }
      }

      // Clean up legacy, non-namespaced flag to prevent cross-user disabling
      localStorage.removeItem(MEETING_SCHEDULED_STORAGE_KEY);
      setHasScheduledMeeting(scheduled);
    }
  }, [user?.id]);

  useEffect(() => {
    if (courseData?.getCourseEnrollmentStatus?.status === "completed") {
      setCompletedSteps([0]); // Mark step 1 (course) as completed
    }
  }, [courseData]);

  if (loading) {
    return (
      <div>
        <CircularProgress sx={{ color: '#00A06D' }} />
      </div>
    );
  }

  if (accountType === "homeowner") {
    // Check step completion status
    const courseStatus = courseData?.getCourseEnrollmentStatus?.status;
    const courseCompleted =
      courseStatus === "completed" ||
      (user as any)?.courseEnrollment?.status === "completed" ||
      (user as any)?.applicationStatus === "course_completed";
    const stepStatus = homeownerStepStatusData?.getHomeownerStepStatus;
    const stepCompletion = stepStatus?.profile?.stepCompletion;
    const completedSteps = stepStatus?.completedSteps ?? [];
    const matchingProfileCompletedBySteps = completedSteps.includes("priorities");
    const matchingProfileCompleted =
      (stepCompletion
        ? stepCompletion.personalityCompleted &&
          stepCompletion.styleCompleted &&
          stepCompletion.projectCompleted &&
          stepCompletion.preferenceCompleted &&
          stepCompletion.prioritiesCompleted
        : false) || matchingProfileCompletedBySteps;
    const projectCompleted = Boolean(homeownerProjectsData?.getHomeOwnerProjects?.length);
    
    const homeownerSteps = [
      {
        id: 1,
        title: "Take Course",
        description: "Working with Designer 101",
        actionLabel: "Go to Course",
        to: "#",
        completed: courseCompleted,
      },
      {
        id: 2,
        title: "Complete Your Matching Profile",
        description:
          "Finish setting up your account to get your matching started.",
        actionLabel: "Set up Matching Profile",
        to: Pages.MY_MATCHING_PROFILE,
        completed: matchingProfileCompleted,
        unlocked: courseCompleted, // Unlocks after course is completed
      },
      {
        id: 3,
        title: "Launch Your First Project",
        description:
          "Share a few details about your space, goals, and budget so our Smart-Guild matching technology can connect you with the right vetted designers.",
        actionLabel: "Go to Projects",
        to: Pages.MY_PROJECTS,
        completed: projectCompleted,
        unlocked: matchingProfileCompleted, // Unlocks after matching profile is completed
      },
    ];
  
    const handleStepClick = async (stepIndex: number) => {
      if (stepIndex === 0) {
        // Handle "Go to Course" click - Step 1
        
        // Validate user is logged in
        if (!user?.id) {
          alert("Please log in to access the course.");
          return;
        }
        
        try {
          const result = await startCourseEnrollmentMutation({
            variables: {
              userId: user.id,
              courseId: "homeowner-101-course",
              // No CRM webhook needed - course completion is tracked via /webhook/course-completion
              crmWebhookUrl: undefined,
            },
          });

          if (result.data?.startCourseEnrollment?.success) {
            // Open the course in a new window/tab
            const courseUrl = result.data.startCourseEnrollment.courseUrl;
            if (courseUrl && user.id) {
              // Add userId parameter to course URL for completion webhook
              const urlWithUserId = courseUrl.includes('?') 
                ? `${courseUrl}&userId=${encodeURIComponent(user.id)}` 
                : `${courseUrl}?userId=${encodeURIComponent(user.id)}`;
              
              window.open(urlWithUserId, "_blank");
            } else {
              alert("Unable to open course. Please try again.");
            }
            
            // Don't mark as complete here - wait for actual course completion
            // Completion status will be updated via the database when user finishes the course
          } else {
            alert("Failed to start course enrollment. Please try again.");
          }
        } catch (error) {
          alert("An error occurred while starting the course. Please try again.");
        }
      } else if (stepIndex === 1) {
        // Handle "Set up Matching Profile" click - Step 2
        // Check if course is completed - if so, allow navigation even if button appears disabled
        if (courseCompleted) {
          navigate(Pages.MY_MATCHING_PROFILE);
        } else {
          alert('Please complete the course first to unlock your matching profile.');
        }
      } else if (stepIndex === 2) {
        // Handle "Go to Projects" click - Step 3
        navigate(Pages.MY_PROJECTS);
      }
    };
  
    return (
      <Stack
        direction="column"
        spacing={2}
        className="content-block application-status-block"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #E0E0E0"
          pb={1}
        >
          <Typography variant="h6" fontWeight={700}>
            Get Started
          </Typography>
        </Box>
  
        {homeownerSteps.map((step, i) => {
          const isComplete = step.completed || (i === 0 && courseCompleted);
          // Step is active if:
          // - It's the first step (course), OR
          // - Previous step is completed, OR
          // - For step 2 (matching profile), course must be completed
          // - For step 3 (bio), matching profile must be completed
          const isActive = !isComplete && (
            i === 0 || // First step is always active if not completed
            (i === 1 && courseCompleted) || // Step 2 unlocks after course
            (i === 2 && matchingProfileCompleted) || // Step 3 unlocks after matching profile
            (i > 0 && homeownerSteps[i - 1].completed) // Previous step completed
          );
          // For step 2 (matching profile), allow clicking if course is completed, even if step appears complete
          const canClickStep2 = i === 1 && courseCompleted;
          const isDisabled = !isActive && !canClickStep2;
          
  
          return (
            <Box key={step.title}>
              <IconTitleDescription
                icon={CheckIcon}
                className={isComplete ? "complete" : isActive ? "active" : ""}
                title={step.title}
                description={step.description}
                callToAction={
                  (isActive || canClickStep2) ? (
                    <Button
                      onClick={() => {
                        handleStepClick(i);
                        if (step.id === 3 && onComplete) {
                          onComplete();
                        }
                      }}
                      variant="text"
                      color={"purple" as any}
                      endIcon={<EastIcon />}
                      className="thin"
                      disabled={courseLoading}
                    >
                      {courseLoading && i === 0 ? "Starting Course..." : step.actionLabel}
                    </Button>
                  ) : (
                    <Button
                      disabled
                      variant="text"
                      color="inherit"
                      endIcon={<EastIcon />}
                      className="thin"
                    >
                      {step.actionLabel}
                    </Button>
                  )
                }
              />
            </Box>
          );
        })}
      </Stack>
    );
  }
  

      const stepButtons = {
        [DesignerApplicationSteps.CompleteApplication]: (
          <Button
            color={"purple" as any}
            variant="text"
            onClick={() => navigate("/designer-application")}
            endIcon={<EastIcon />}
            className="thin"
            sx={{ fontWeight: 500, fontSize: '14px' }}
          >
            <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
              Go to application
            </Typography>
          </Button>
        ),
        [DesignerApplicationSteps.UnderReview]: (
          hasScheduledMeeting ? (
            <Box
              sx={{
                textDecoration: 'line-through',
                opacity: 0.7,
                '& .MuiButton-root': { pointerEvents: 'none', textDecoration: 'line-through' },
              }}
            >
              <Button
                color={"purple" as any}
                variant="text"
                className="thin"
                sx={{ fontWeight: 500, fontSize: '14px' }}
              >
                <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                  Meeting Scheduled
                </Typography>
              </Button>
            </Box>
          ) : (
            <Button
              component={Link}
              color={"purple" as any}
              variant="text"
              to={calendarLink}
              endIcon={<EastIcon />}
              target="_blank"
              className="thin"
              sx={{ fontWeight: 500, fontSize: '14px' }}
            >
              <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                Schedule Meeting
              </Typography>
            </Button>
          )
        ),
        [DesignerApplicationSteps.Approved]: (
          <Button
            color={"purple" as any}
            variant="text"
            onClick={() => navigate(Pages.DESIGNER_MEMBERSHIP_SELECTION)}
            endIcon={<EastIcon />}
            className="thin"
            sx={{ fontWeight: 500, fontSize: '14px' }}
          >
            <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
              Choose Membership Level & Pay
            </Typography>
          </Button>
        ),
      };

      // Helper function to render button with strike-through for completed steps
      const renderStepButton = (stepEnum: DesignerApplicationSteps, isComplete: boolean) => {
        const button = (stepButtons as any)[stepEnum];
        if (!button) return null;

        if (isComplete) {
          // Clone the button and add strike-through styling
          return (
            <Box sx={{ 
              textDecoration: 'line-through',
              opacity: 0.7,
              '& .MuiButton-root': {
                textDecoration: 'line-through',
                opacity: 0.7,
                pointerEvents: 'none' // Disable clicking on completed steps
              }
            }}>
              {button}
            </Box>
          );
        }
        
        return button;
      };

  // For users with MembershipActive status, we want to show the full progression
  // with Membership Active as the current step
  const userOnboardingStatus = (user as any)?.onboardingStatus;
  const userPaymentStatus = (user as any)?.paymentStatus;
  const userSubscription = (user as any)?.subscription;
  const normalizedSubscription = normalizeSubscriptionToken(userSubscription);
  const subscriptionToken = normalizedSubscription.replace(/[\s_-]+/g, "");
  const hasPaidMembershipFromSubscription =
    Boolean(subscriptionToken) &&
    !["free", "basic", "home", "homemonthly", "homebiannually"].includes(
      subscriptionToken
    );
  const hasTrustedMembershipEvidence =
    userOnboardingStatus === "active" ||
    userPaymentStatus === "paid" ||
    hasPaidMembershipFromSubscription;

  const membershipActiveFromUser =
    userOnboardingStatus === 'active' ||
    userPaymentStatus === 'paid' ||
    hasPaidMembershipFromSubscription;

  const getEffectiveCurrentStatus = () => {
    // If application status is explicitly provided, trust it
    if (currentStatus) {
      // Handle ToBeCompleted status - this means application hasn't been started yet
      // Treat it as CompleteApplication (first step)
      if (currentStatus === DesignerApplicationSteps.ToBeCompleted) {
        return DesignerApplicationSteps.CompleteApplication;
      }

      // Check if user has membership active indicators
      if (currentStatus === DesignerApplicationSteps.Approved && membershipActiveFromUser) {
        return DesignerApplicationSteps.MembershipActive;
      }
      return currentStatus;
    }

    // If no application status provided, check user's onboarding status
    // For new designers with pending_profile_completion, they should start at CompleteApplication
    // NOTE: 'profile_completed' is a legacy invalid status - treat it like 'pending_profile_completion'
    if (userOnboardingStatus === 'pending_profile_completion' || userOnboardingStatus === 'profile_completed') {
      return DesignerApplicationSteps.CompleteApplication;
    }

    // For designers with pending_email_verification, they shouldn't see this component
    if (userOnboardingStatus === 'pending_email_verification') {
      return DesignerApplicationSteps.CompleteApplication;
    }

    // Only show MembershipActive if we have clear evidence
    if (membershipActiveFromUser) {
      return DesignerApplicationSteps.MembershipActive;
    }

    // Default to CompleteApplication for safety
    return DesignerApplicationSteps.CompleteApplication;
  };

  const effectiveCurrentStatus = getEffectiveCurrentStatus();
  const activeIndex = stepsInOrder.indexOf(effectiveCurrentStatus);
  
  // Debug the status calculation
  
  // Debug logging
  
  // More detailed debug logging

  // Define the steps to show based on current status
  const getStepsToShow = () => {
    const baseSteps = [
      DesignerApplicationSteps.CompleteApplication,
      DesignerApplicationSteps.UnderReview,
      DesignerApplicationSteps.Approved,
      DesignerApplicationSteps.MembershipActive,
    ];
    
    // Only include Rejected if the status is actually rejected
    if (currentStatus === DesignerApplicationSteps.Rejected) {
      return [DesignerApplicationSteps.CompleteApplication, DesignerApplicationSteps.Rejected];
    }
    
    return baseSteps;
  };

  const stepsToShow = getStepsToShow().filter(step => 
    step in stepTitles && step in stepDescriptions
  );

  return (
    <Stack
      direction="column"
      spacing={2}
      className="content-block application-status-block"
    >
          {stepsToShow.map((stepEnum, i) => {
            // Fix the active step logic - if activeIndex is -1, make the first step active
            const isActive = activeIndex === i || (activeIndex === -1 && i === 0 && stepEnum === DesignerApplicationSteps.CompleteApplication);
            // Only mark steps as complete if activeIndex is valid and greater than current index
            // For ToBeCompleted status, no steps should be marked as complete
            // Special case: if MembershipActive, mark Approved as complete
            const isComplete = 
              (activeIndex > i && activeIndex !== -1 && (currentStatus !== DesignerApplicationSteps.ToBeCompleted)) ||
              (effectiveCurrentStatus === DesignerApplicationSteps.MembershipActive && stepEnum === DesignerApplicationSteps.Approved);

            return (
              <Box key={stepEnum}>
                <IconTitleDescription
                  className={isActive ? "active" : isComplete ? "complete" : ""}
                  icon={CheckIcon}
                  title={stepTitles[stepEnum as keyof typeof stepTitles]}
                  description={stepDescriptions[stepEnum as keyof typeof stepDescriptions]}
                  callToAction={
                    // Show button for active step or completed steps
                    (isActive || isComplete) ? renderStepButton(stepEnum, isComplete) : null
                  }
                />

                {(effectiveCurrentStatus === DesignerApplicationSteps.MembershipActive) &&
                  stepEnum === DesignerApplicationSteps.MembershipActive && (
                    <Stack
                      spacing={1}
                      sx={{ maxWidth: "95%", mx: "auto", mt: 2 }}
                    >
                      <Stack
                        justifyContent="space-between"
                        direction="row"
                        alignItems="center"
                        spacing={2}
                      >
                        <Typography 
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: onboardingStatus?.matchingProfileComplete ? 'text.disabled' : 'text.primary',
                            textDecoration: onboardingStatus?.matchingProfileComplete ? 'line-through' : 'none'
                          }}
                        >
                          Complete Your Matching Profile
                        </Typography>
                        {onboardingStatus?.matchingProfileComplete ? (
                          <Box sx={{ 
                            textDecoration: 'line-through',
                            opacity: 0.7,
                            '& .MuiButton-root': {
                              textDecoration: 'line-through',
                              opacity: 0.7,
                              pointerEvents: 'none' // Disable clicking on completed steps
                            }
                          }}>
                            <Button
                              component={Link}
                              color={"purple" as any}
                              variant="text"
                              to={Pages.MY_MATCHING_PROFILE}
                              endIcon={<EastIcon />}
                              className="thin"
                              disabled={true}
                              sx={{ fontWeight: 500, fontSize: '14px' }}
                            >
                              <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                                Re-take Matching Profile
                              </Typography>
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            component={Link}
                            color={"purple" as any}
                            variant="text"
                            to={Pages.MY_MATCHING_PROFILE}
                            endIcon={<EastIcon />}
                            className="thin"
                            sx={{ fontWeight: 500, fontSize: '14px' }}
                          >
                            <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                              Set up Matching Profile
                            </Typography>
                          </Button>
                        )}
                      </Stack>

                      <Divider />

                      <Stack
                        justifyContent="space-between"
                        direction="row"
                        alignItems="center"
                        spacing={2}
                      >
                        <Typography 
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: onboardingStatus?.bioComplete ? 'text.disabled' : 'text.primary',
                            textDecoration: onboardingStatus?.bioComplete ? 'line-through' : 'none'
                          }}
                        >
                          Complete Your Designer Bio
                        </Typography>
                        {onboardingStatus?.bioComplete ? (
                          <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px', color: 'text.disabled' }}>
                            Complete
                          </Typography>
                        ) : (
                          <Button
                            component={Link}
                            color={"purple" as any}
                            variant="text"
                            to={Pages.MY_BIO}
                            endIcon={<EastIcon />}
                            className="thin"
                            sx={{ fontWeight: 500, fontSize: '14px' }}
                          >
                            <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                              Complete my Bio
                            </Typography>
                          </Button>
                        )}
                      </Stack>

                      <Divider />

                      <Stack
                        justifyContent="space-between"
                        direction="row"
                        alignItems="center"
                        spacing={2}
                      >
                        <Typography 
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: (onboardingStatus?.designFilesComplete && onboardingStatus?.bioComplete) ? 'text.disabled' : 'text.primary',
                            textDecoration: (onboardingStatus?.designFilesComplete && onboardingStatus?.bioComplete) ? 'line-through' : 'none'
                          }}
                        >
                          Load Your Designer Files
                        </Typography>
                        {/* Only show as complete if both bio and design files are complete (full onboarding done) */}
                        {(onboardingStatus?.designFilesComplete && onboardingStatus?.bioComplete) ? (
                          <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px', color: 'text.disabled' }}>
                            Complete
                          </Typography>
                        ) : (
                          <Button
                            component={Link}
                            color={"purple" as any}
                            variant="text"
                            to={Pages.MY_DESIGN_FILES}
                            endIcon={<EastIcon />}
                            className="thin"
                            sx={{ fontWeight: 500, fontSize: '14px' }}
                          >
                            <Typography variant="body2" component="span" sx={{ fontWeight: 500, fontSize: '14px' }}>
                              Load Designer Files
                            </Typography>
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  )}
              </Box>
            );
          })}
    </Stack>
  );
};
